/**
 * Coordinates native Shopify variant options for player personalization.
 * Handles picker re-renders by querying the current controls on every change.
 */
class PlayerPersonalization extends HTMLElement {
  connectedCallback() {
    this.variantPicker = this.closest('variant-picker');
    if (!this.variantPicker) return;

    // Shopify morphs the variant-picker HTML after a selection. Always query the
    // current fields instead of retaining references to nodes that may be replaced.
    // Custom name/number inputs were intentionally removed: the store will confirm
    // the requested personalization with the customer by WhatsApp after the order.
    this.isSettingPlayer = false;
    this.syncTimer = null;
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.scheduleSync = this.scheduleSync.bind(this);
    // Capture before the theme's bubbling variant handler can re-render/reset the picker.
    this.variantPicker.addEventListener('change', this.handleChange, true);
    document.addEventListener('submit', this.handleSubmit, true);

    // The theme replaces/morphs variant option markup asynchronously after the
    // change event. Re-sync once that DOM update has settled so custom fields do
    // not flash open and then get hidden by the incoming HTML.
    this.mutationObserver = new MutationObserver(this.scheduleSync);
    this.mutationObserver.observe(this.variantPicker, { childList: true, subtree: true });
    this.syncState({ initialize: true });
  }

  disconnectedCallback() {
    this.variantPicker?.removeEventListener('change', this.handleChange, true);
    document.removeEventListener('submit', this.handleSubmit, true);
    this.mutationObserver?.disconnect();
    if (this.syncTimer) clearTimeout(this.syncTimer);
  }

  scheduleSync() {
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      if (this.isConnected) this.syncState();
    }, 40);
  }

  get customFields() {
    return this.querySelector('[data-custom-fields]');
  }

  get customName() {
    return this.querySelector('[data-custom-name]');
  }

  get customNumber() {
    return this.querySelector('[data-custom-number]');
  }

  get personalizationSelect() {
    return this.variantPicker?.querySelector('select[name="options[Personalizado]"]');
  }

  get playerSelect() {
    return this.variantPicker?.querySelector('select[name="options[Jugador]"]');
  }

  get playerOptionWrapper() {
    return this.playerSelect?.closest('.variant-option');
  }

  handleChange(event) {
    if (this.isSettingPlayer) return;
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    if (target.name === 'options[Jugador]') {
      // Remember the player's explicit choice across Shopify's asynchronous
      // variant-picker re-render. This is essential for the Personalizado value.
      this.variantPicker.dataset.requestedPlayer = target.value;
      this.syncState();
      return;
    }

    if (target.name === 'options[Personalizado]') {
      this.syncState();
    }
  }

  setPlayerValue(value) {
    const select = this.playerSelect;
    if (!select) return false;
    const option = Array.from(select.options).find((item) => item.value === value);
    if (!option || option.disabled || select.value === value) return false;

    this.isSettingPlayer = true;
    select.value = value;
    // Notify Shopify's native variant-picker so the actual variant ID is updated.
    select.dispatchEvent(new Event('change', { bubbles: true }));
    this.isSettingPlayer = false;
    return true;
  }

  applyPlayerVisibility(basicSelected) {
    const picker = this.variantPicker;
    if (!picker) return;

    // The picker may replace its inner option markup after a variant change.
    // Keep a state attribute on the stable custom element so CSS hides any new markup too.
    picker.toggleAttribute('data-player-basic-hidden', basicSelected);
    const select = this.playerSelect;
    const wrapper = this.playerOptionWrapper;
    if (wrapper) wrapper.hidden = basicSelected;
    if (select) select.disabled = basicSelected;

    // Re-apply after Shopify's variant-picker has had a chance to update its DOM.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const currentPicker = this.variantPicker;
      const currentSelect = this.playerSelect;
      const currentWrapper = this.playerOptionWrapper;
      if (!currentPicker || !currentSelect) return;
      currentPicker.toggleAttribute('data-player-basic-hidden', basicSelected);
      if (currentWrapper) currentWrapper.hidden = basicSelected;
      currentSelect.disabled = basicSelected;
    });
  }

  syncState({ initialize = false } = {}) {
    const personalization = this.personalizationSelect;
    const player = this.playerSelect;
    if (!personalization || !player) return;

    const basicSelected = personalization.value === (this.dataset.basicValue || 'Básica');
    const noPlayerValue = this.dataset.basicPlayerValue || 'Sin jugador';
    const customValue = this.dataset.customPlayerValue || 'Personalizado';

    // Básica must always map to the real no-player variant. For other modes,
    // restore only a player the customer explicitly chose; never auto-select Messi
    // or another first option just because Shopify re-rendered the picker.
    if (basicSelected) {
      this.variantPicker.dataset.requestedPlayer = noPlayerValue;
      if (player.value !== noPlayerValue) this.setPlayerValue(noPlayerValue);
    } else {
      const requestedPlayer = this.variantPicker.dataset.requestedPlayer;
      if (requestedPlayer && player.value !== requestedPlayer) {
        const requestedOption = Array.from(player.options).find((option) => option.value === requestedPlayer);
        if (requestedOption && !requestedOption.disabled) this.setPlayerValue(requestedPlayer);
      } else if (player.value === customValue) {
        this.variantPicker.dataset.requestedPlayer = customValue;
      }
    }

    this.applyPlayerVisibility(basicSelected);
    this.syncCustomFields();
  }

  syncCustomFields() {
    const personalization = this.personalizationSelect;
    const player = this.playerSelect;
    const fields = this.customFields;
    const nameInput = this.customName;
    const numberInput = this.customNumber;
    if (!personalization || !player || !fields || !nameInput || !numberInput) return;

    const basicSelected = personalization.value === (this.dataset.basicValue || 'Básica');
    const customSelected = !basicSelected && player.value === (this.dataset.customPlayerValue || 'Personalizado');
    fields.hidden = !customSelected;
    fields.toggleAttribute('data-visible', customSelected);
    nameInput.disabled = !customSelected;
    numberInput.disabled = !customSelected;
    nameInput.required = customSelected;
    numberInput.required = customSelected;

    if (!customSelected) {
      nameInput.value = '';
      numberInput.value = '';
    }
  }

  handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== this.dataset.formId) return;

    const personalization = this.personalizationSelect;
    const player = this.playerSelect;
    if (!personalization || !player) return;

    const basicSelected = personalization.value === (this.dataset.basicValue || 'Básica');
    const playerValue = player.value;
    const noPlayerValue = this.dataset.basicPlayerValue || 'Sin jugador';
    const customValue = this.dataset.customPlayerValue || 'Personalizado';

    if (basicSelected) {
      const hasNoPlayerValue = Array.from(player.options).some((option) => option.value === noPlayerValue);
      if (!hasNoPlayerValue) {
        event.preventDefault();
        event.stopImmediatePropagation();
        alert('Para vender la camiseta Básica sin jugador, agrega «Sin jugador» como valor de la opción Jugador y crea las variantes correspondientes para cada talla.');
        return;
      }
      if (playerValue !== noPlayerValue) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setPlayerValue(noPlayerValue);
        alert('Se actualizó la variante sin jugador. Pulsa nuevamente para agregarla al carrito.');
      }
      return;
    }

    if (!playerValue || playerValue === noPlayerValue) {
      event.preventDefault();
      event.stopImmediatePropagation();
      player.focus();
      alert('Selecciona un jugador para continuar.');
      return;
    }

    // If «Personalizado» is selected, no text fields are collected at checkout.
    // The store will contact the customer via WhatsApp to confirm the details.
  }
}

if (!customElements.get('player-personalization')) {
  customElements.define('player-personalization', PlayerPersonalization);
}
